import { classifyColorSamples } from './colorUtils';
import { getControlLayout } from './modes/control/layouts';

const SAMPLE_COLUMNS = [0.08, 0.16, 0.24, 0.34, 0.48, 0.62, 0.76, 0.9];
const SAMPLE_ROWS = [0.28, 0.5, 0.72];

const toPixelBounds = (bounds, width, height) => ({
  x: Math.round(bounds.x * width),
  y: Math.round(bounds.y * height),
  width: Math.round(bounds.width * width),
  height: Math.round(bounds.height * height),
});

const samplePatchAverage = (context, x, y, radius = 2) => {
  const patchX = Math.max(0, x - radius);
  const patchY = Math.max(0, y - radius);
  const patchSize = (radius * 2) + 1;
  const imageData = context.getImageData(patchX, patchY, patchSize, patchSize);
  const colors = [];

  for (let index = 0; index < imageData.data.length; index += 4) {
    colors.push({
      r: imageData.data[index],
      g: imageData.data[index + 1],
      b: imageData.data[index + 2],
    });
  }

  const total = colors.reduce((acc, color) => ({
    r: acc.r + color.r,
    g: acc.g + color.g,
    b: acc.b + color.b,
  }), { r: 0, g: 0, b: 0 });

  return {
    r: Math.round(total.r / colors.length),
    g: Math.round(total.g / colors.length),
    b: Math.round(total.b / colors.length),
  };
};

const sampleRowColors = (context, bounds, imageWidth, imageHeight) => {
  const pixelBounds = toPixelBounds(bounds, imageWidth, imageHeight);

  return SAMPLE_COLUMNS.flatMap((xRatio) => SAMPLE_ROWS.map((yRatio) => {
    const x = Math.min(imageWidth - 1, Math.max(0, pixelBounds.x + Math.round(pixelBounds.width * xRatio)));
    const y = Math.min(imageHeight - 1, Math.max(0, pixelBounds.y + Math.round(pixelBounds.height * yRatio)));
    return samplePatchAverage(context, x, y);
  }));
};

const summarizeRows = (rows) => rows.reduce((acc, row) => {
  acc[row.teamColor] += 1;
  return acc;
}, { blue: 0, red: 0, unknown: 0 });

const getWarning = ({ category, rows, summary, layout }) => {
  if (category === 'propDetails') {
    return summary.blue + summary.red < layout.warningThreshold
      ? 'Detected very few colored prop rows. Check row boxes before parsing later.'
      : '';
  }

  const expectedBlue = layout.expected?.blue || 0;
  const expectedRed = layout.expected?.red || 0;
  const blueDelta = Math.abs(summary.blue - expectedBlue);
  const redDelta = Math.abs(summary.red - expectedRed);

  if (blueDelta >= layout.warningThreshold || redDelta >= layout.warningThreshold || summary.unknown > 2) {
    return `Expected approximately ${expectedBlue} Blue / ${expectedRed} Red rows. Detected ${summary.blue} Blue / ${summary.red} Red / ${summary.unknown} Unknown.`;
  }

  if (!rows.length) {
    return 'No candidate rows were available for this screenshot layout.';
  }

  return '';
};

export const filterRowsByTeam = (rows, selectedTeamColor) => (
  rows.filter((row) => row.teamColor === selectedTeamColor)
);

const detectTeamRows = ({ category, processedImage, selectedTeamColor = 'blue' }) => {
  if (!processedImage?.canvas) {
    throw new Error('Processed image is required for team row detection.');
  }

  const startedAt = performance.now();
  const { canvas } = processedImage;
  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    throw new Error('Could not read screenshot pixels.');
  }

  const layout = getControlLayout(category);
  const rows = layout.rows.map((candidate, index) => {
    const samples = sampleRowColors(context, candidate.bounds, canvas.width, canvas.height);
    const classification = classifyColorSamples(samples);
    const pixelBounds = toPixelBounds(candidate.bounds, canvas.width, canvas.height);

    return {
      id: candidate.id || `row-${index + 1}`,
      rowIndex: candidate.rowIndex ?? index,
      teamColor: classification.teamColor,
      confidence: Number(classification.confidence.toFixed(2)),
      bounds: pixelBounds,
      normalizedBounds: candidate.bounds,
      votes: classification.votes,
      averageColor: classification.averageColor,
      averageHsv: {
        h: Math.round(classification.averageHsv.h),
        s: Number(classification.averageHsv.s.toFixed(2)),
        v: Number(classification.averageHsv.v.toFixed(2)),
      },
      sampleCount: classification.sampleCount,
    };
  });

  const summary = summarizeRows(rows);
  const selectedRows = filterRowsByTeam(rows, selectedTeamColor);

  return {
    category,
    selectedTeamColor,
    rows,
    selectedRows,
    summary,
    warning: getWarning({ category, rows, summary, layout }),
    image: {
      width: canvas.width,
      height: canvas.height,
      originalWidth: processedImage.originalWidth,
      originalHeight: processedImage.originalHeight,
      scale: processedImage.scale,
    },
    elapsedMs: Math.round(performance.now() - startedAt),
  };
};

export default detectTeamRows;
